// Additive offer-card footer: product_name, funnel_url, offer_code badge,
// created/updated dates, + Edit (modal) and Delete (soft-delete) actions.
// Self-contained (no App import) — edit/delete are backend-role-gated.
import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AX = { withCredentials: true };
const STATUSES = ["pending", "discovered", "assigned", "activated", "in_creative", "in_market", "paused", "closed"];
const PAYOUT_TYPES = ["CPA", "CPS", "CPL", "RevShare"];

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleDateString();
};
const stop = (e) => e.stopPropagation();

export default function OfferCardActions({ offer, onChanged }) {
  const { user } = useAuth();
  const canManage = ["FOUNDER", "MEDIA_BUYER"].includes(user?.role);
  const [data, setData] = useState(offer);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({});

  if (removed) return null;

  const openEdit = (e) => {
    stop(e);
    setErr("");
    setForm({
      name: data.name || "",
      product_name: data.product_name || "",
      funnel_url: data.funnel_url || "",
      product_description: data.product_description || "",
      payout: data.payout ?? 0,
      payout_type: data.payout_type || "CPA",
      status: data.status || "discovered",
    });
    setEditing(true);
  };

  const save = async (e) => {
    stop(e);
    setBusy(true); setErr("");
    try {
      const res = await axios.patch(`${API}/offers/${data.offer_id}`, { ...form, payout: Number(form.payout) }, AX);
      setData(res.data);          // refresh card with updated data (incl. updated_at)
      setEditing(false);
      if (onChanged) onChanged(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const doDelete = async (e) => {
    stop(e);
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/offers/${data.offer_id}/delete`, {}, AX);
      setRemoved(true);           // remove from UI immediately
      if (onChanged) onChanged(null);
      else window.location.reload();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };

  const created = fmtDate(data.created_at);
  const updated = fmtDate(data.updated_at);
  const showUpdated = updated && updated !== created;
  const inputCls = "bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 w-full focus:outline-none focus:border-emerald-500/50";

  return (
    <div className="mt-2 pt-2 border-t border-slate-700/40" onClick={stop} data-testid={`offer-card-actions-${data.offer_id}`}>
      {data.product_name && <p className="text-slate-400 text-xs">{data.product_name}</p>}
      {data.funnel_url && (
        <a href={data.funnel_url} target="_blank" rel="noopener noreferrer" onClick={stop}
           className="text-emerald-400 hover:text-emerald-300 text-xs truncate block max-w-full" title={data.funnel_url}>
          {data.funnel_url.replace(/^https?:\/\//, "").slice(0, 48)}{data.funnel_url.length > 55 ? "…" : ""}
        </a>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="flex flex-col gap-0.5">
          {data.offer_code && (
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/40 text-slate-300 text-[10px] font-mono w-fit">
              {data.offer_code}
            </span>
          )}
          <div className="text-[10px] text-slate-500">
            {created && <span>Created {created}</span>}
            {showUpdated && <span className="ml-2">Last edited {updated}</span>}
          </div>
        </div>
        {canManage && (
          <div className="flex gap-1.5">
            <button onClick={openEdit} data-testid="offer-edit-btn"
                    className="px-2 py-1 rounded text-[11px] bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/70">Edit</button>
            <button onClick={(e) => { stop(e); setConfirming(true); }} data-testid="offer-delete-btn"
                    className="px-2 py-1 rounded text-[11px] bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25">Delete</button>
          </div>
        )}
      </div>

      {err && <p className="text-rose-400 text-xs mt-1">{err}</p>}

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => { stop(e); setEditing(false); }}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-auto" onClick={stop}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Edit Offer</h3>
              <button onClick={(e) => { stop(e); setEditing(false); }} className="text-slate-400 hover:text-white text-sm">Close</button>
            </div>
            <div className="space-y-3">
              {[
                ["name", "Offer Name"],
                ["product_name", "Product Name"],
                ["funnel_url", "Funnel URL"],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
                  <input className={inputCls} value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`edit-${k}`} />
                </label>
              ))}
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-500">Description</span>
                <textarea className={inputCls} rows={3} value={form.product_description} onChange={(e) => setForm((p) => ({ ...p, product_description: e.target.value }))} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-slate-500">Payout</span>
                  <input type="number" className={inputCls} value={form.payout} onChange={(e) => setForm((p) => ({ ...p, payout: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-slate-500">Payout Type</span>
                  <select className={inputCls} value={form.payout_type} onChange={(e) => setForm((p) => ({ ...p, payout_type: e.target.value }))}>
                    {PAYOUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-500">Status</span>
                <select className={inputCls} value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              {err && <p className="text-rose-400 text-xs">{err}</p>}
              <button disabled={busy} onClick={save} data-testid="edit-save"
                      className="w-full px-4 py-2 rounded-lg text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50">
                {busy ? "Saving…" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {confirming && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" onClick={(e) => { stop(e); setConfirming(false); }}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-sm p-6" onClick={stop}>
            <h3 className="text-lg font-bold text-white mb-2">Delete offer?</h3>
            <p className="text-slate-400 text-sm mb-4">"{data.name}" will be hidden (soft delete). It stays recoverable and campaigns keep running.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={(e) => { stop(e); setConfirming(false); }} className="px-3 py-1.5 rounded-lg text-sm bg-slate-700/40 text-slate-200 border border-slate-600/40">Cancel</button>
              <button disabled={busy} onClick={doDelete} data-testid="delete-confirm"
                      className="px-3 py-1.5 rounded-lg text-sm bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 disabled:opacity-50">
                {busy ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
